import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  getPartnerOffer,
  updatePartnerOffer,
  getPartnerOfferCategories,
  getOrganizationsForDropdown,
  getAllTags,
  getOfferEventStats,
} from "../services/partnerOffers.service";
import type {
  PartnerOffer,
  PartnerOfferCategory,
  PartnerOfferFormData,
  PartnerOfferStatus,
  PartnerOfferBrand,
  PartnerOfferType,
} from "../types/partnerOffer.types";
// Value import — PartnerOfferKind is used as a value (comparisons + select).
import { PartnerOfferKind } from "../types/partnerOffer.types";

const STATUS_OPTIONS: {
  value: PartnerOfferStatus;
  label: string;
  color: string;
}[] = [
  { value: "draft", label: "Draft", color: "bg-gray-100 text-gray-800" },
  {
    value: "scheduled",
    label: "Scheduled",
    color: "bg-blue-100 text-blue-800",
  },
  { value: "active", label: "Active", color: "bg-green-100 text-green-800" },
  { value: "paused", label: "Paused", color: "bg-yellow-100 text-yellow-800" },
  { value: "expired", label: "Expired", color: "bg-red-100 text-red-800" },
  { value: "archived", label: "Archived", color: "bg-gray-100 text-gray-600" },
];

const BRAND_OPTIONS = [
  { value: "both", label: "Both Brands" },
  { value: "usls_only", label: "USLS Only" },
  { value: "emergency_shield_only", label: "EmergencyShield Only" },
];

const TYPE_OPTIONS = [
  { value: "both", label: "All States" },
  { value: "insurance", label: "Insurance States Only" },
  { value: "non_insurance", label: "Non-Insurance States Only" },
];

interface Organization {
  id: string;
  name: string;
}

const PartnerOfferDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [offer, setOffer] = useState<PartnerOffer | null>(null);
  const [categories, setCategories] = useState<PartnerOfferCategory[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [existingTags, setExistingTags] = useState<string[]>([]);
  const [stats, setStats] = useState({
    impressions: 0,
    clicks: 0,
    claims: 0,
    redemptions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    "details" | "targeting" | "content" | "analytics"
  >("details");

  // Form state
  const [formData, setFormData] = useState<PartnerOfferFormData | null>(null);
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [offerData, categoriesData, orgsData, tagsData] = await Promise.all(
        [
          getPartnerOffer(id!),
          getPartnerOfferCategories(),
          getOrganizationsForDropdown(),
          getAllTags(),
        ],
      );

      if (!offerData) {
        setError("Offer not found");
        return;
      }

      setOffer(offerData);
      setCategories(categoriesData);
      setOrganizations(orgsData);
      setExistingTags(tagsData);

      // Load stats
      const statsData = await getOfferEventStats(id!);
      setStats(statsData);

      // Initialize form data from offer
      setFormData({
        external_id: offerData.external_id,
        company_name: offerData.company_name,
        company_logo_url: offerData.company_logo_url,
        headline: offerData.headline,
        description: offerData.description,
        short_description: offerData.short_description,
        offer_amount: offerData.offer_amount,
        offer_link: offerData.offer_link,
        promo_code: offerData.promo_code,
        legal_disclaimer: offerData.legal_disclaimer,
        terms_and_conditions: offerData.terms_and_conditions,
        redemption_instructions: offerData.redemption_instructions,
        image_url: offerData.image_url,
        category_id: offerData.category_id,
        source_organization_id: offerData.source_organization_id,
        tags: offerData.tags || [],
        offer_kind: offerData.offer_kind,
        start_date: offerData.start_date,
        end_date: offerData.end_date,
        start_time: offerData.start_time,
        end_time: offerData.end_time,
        timezone: offerData.timezone,
        brand_target: offerData.brand_target,
        offer_type: offerData.offer_type,
        target_states: offerData.target_states,
        target_all_states: offerData.target_all_states,
        is_featured: offerData.is_featured,
        priority_score: offerData.priority_score,
        status: offerData.status,
        max_redemptions: offerData.max_redemptions,
        redemptions_per_member: offerData.redemptions_per_member,
      });
    } catch (err) {
      console.error("Error loading offer:", err);
      setError("Failed to load offer");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData || !id) {
      return;
    }
    // FR-05: Lightning offers require dates; Standing offers store NULL dates.
    if (
      formData.offer_kind === PartnerOfferKind.LIGHTNING &&
      (!formData.start_date || !formData.end_date)
    ) {
      setError("Lightning offers require a Start and End date.");
      return;
    }
    if (
      formData.offer_kind === PartnerOfferKind.LIGHTNING &&
      formData.start_date &&
      formData.end_date &&
      formData.end_date < formData.start_date
    ) {
      setError("End Date must be on or after the Start Date.");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const payload: PartnerOfferFormData =
        formData.offer_kind === PartnerOfferKind.STANDING
          ? { ...formData, start_date: null, end_date: null }
          : formData;
      await updatePartnerOffer(id, payload);
      setSuccess("Offer saved successfully");
      loadData();
    } catch (err) {
      console.error("Error saving offer:", err);
      setError("Failed to save offer");
    } finally {
      setSaving(false);
    }
  };

  const handleAddTag = () => {
    if (!tagInput.trim() || !formData) {
      return;
    }
    const newTag = tagInput.trim().toLowerCase();
    if (!formData.tags?.includes(newTag)) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), newTag],
      });
    }
    setTagInput("");
  };

  const handleRemoveTag = (tagToRemove: string) => {
    if (!formData) {
      return;
    }
    setFormData({
      ...formData,
      tags: formData.tags?.filter((t) => t !== tagToRemove) || [],
    });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!offer || !formData) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error || "Offer not found"}
        </div>
        <Link
          to="/partner-offers"
          className="mt-4 inline-block text-primary-600 hover:underline"
        >
          Back to offers
        </Link>
      </div>
    );
  }

  const currentStatus = STATUS_OPTIONS.find((s) => s.value === formData.status);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Link
              to="/partner-offers"
              className="text-gray-500 hover:text-gray-700"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">
              {offer.headline}
            </h1>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${currentStatus?.color}`}
            >
              {currentStatus?.label}
            </span>
          </div>
          <p className="text-gray-600">
            {offer.company_name ||
              offer.source_organization?.name ||
              "US Law Shield"}
            {offer.company_name && offer.source_organization && (
              <span className="ml-2 text-sm text-gray-400">
                (via {offer.source_organization.name})
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {success}
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-900">
            {stats.impressions}
          </div>
          <div className="text-sm text-gray-500">Impressions</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-900">{stats.clicks}</div>
          <div className="text-sm text-gray-500">Clicks</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-900">{stats.claims}</div>
          <div className="text-sm text-gray-500">Claims</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-primary-600 font-mono">
            {offer.offer_amount}
          </div>
          <div className="text-sm text-gray-500">Offer Value</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "details", label: "Basic Details" },
            { id: "content", label: "Content & Media" },
            { id: "targeting", label: "Targeting & Rules" },
            { id: "analytics", label: "Analytics" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {activeTab === "details" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                {/* FR-03: Source Organization is the offer identity (the
                    free-text Company Name box was removed). */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Source Organization
                  </label>
                  <select
                    value={formData.source_organization_id || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        source_organization_id: e.target.value || undefined,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">US Law Shield (Internal)</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    The organization this offer belongs to (blank = internal
                    USLS offer). Shown to members as the offer's source.
                  </p>
                </div>

                {/* FR-04: Standing vs Lightning — drives whether dates apply. */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Offer Type *
                  </label>
                  <select
                    value={formData.offer_kind}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        offer_kind: e.target.value as PartnerOfferKind,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value={PartnerOfferKind.STANDING}>
                      Standing — always available (no dates)
                    </option>
                    <option value={PartnerOfferKind.LIGHTNING}>
                      Lightning — limited-time (start &amp; end dates)
                    </option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.offer_kind === PartnerOfferKind.LIGHTNING
                      ? "Runs only between the dates you set."
                      : "Stays active indefinitely — no start/end dates."}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={formData.category_id || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        category_id: e.target.value || undefined,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select category...</option>
                    {categories.map((cat) => (
                      <React.Fragment key={cat.id}>
                        <option value={cat.id}>{cat.name}</option>
                        {cat.children?.map((child) => (
                          <option key={child.id} value={child.id}>
                            &nbsp;&nbsp;&nbsp;{child.name}
                          </option>
                        ))}
                      </React.Fragment>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Headline *
                  </label>
                  <input
                    type="text"
                    value={formData.headline}
                    onChange={(e) =>
                      setFormData({ ...formData, headline: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Offer Amount *
                  </label>
                  <input
                    type="text"
                    value={formData.offer_amount}
                    onChange={(e) =>
                      setFormData({ ...formData, offer_amount: e.target.value })
                    }
                    placeholder="e.g., 20% off, $50, Free"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status: e.target.value as PartnerOfferStatus,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* FR-05: dates only for Lightning (time-boxed) offers. */}
                {formData.offer_kind === PartnerOfferKind.LIGHTNING ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date *
                      </label>
                      <input
                        type="date"
                        value={formData.start_date ?? ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            start_date: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date *
                      </label>
                      <input
                        type="date"
                        value={formData.end_date ?? ""}
                        onChange={(e) =>
                          setFormData({ ...formData, end_date: e.target.value })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                ) : (
                  <p className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-600">
                    <strong>Standing</strong> offer — always active, no
                    start/end date. Switch Offer Type to{" "}
                    <strong>Lightning</strong> to set a window.
                  </p>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority (1-100)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={formData.priority_score}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          priority_score: parseInt(e.target.value) || 50,
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_featured}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            is_featured: e.target.checked,
                          })
                        }
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Featured Offer
                      </span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.tags?.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-800 rounded-full text-sm"
                      >
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 text-primary-600 hover:text-primary-800"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleAddTag())
                      }
                      placeholder="Add a tag..."
                      list="existing-tags"
                      className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                    <datalist id="existing-tags">
                      {existingTags.map((tag) => (
                        <option key={tag} value={tag} />
                      ))}
                    </datalist>
                    <button
                      onClick={handleAddTag}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "content" && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Short Description
              </label>
              <input
                type="text"
                value={formData.short_description || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    short_description: e.target.value,
                  })
                }
                placeholder="Brief summary for list views"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Description
              </label>
              <textarea
                value={formData.description || ""}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={4}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Offer Link
                </label>
                <input
                  type="url"
                  value={formData.offer_link || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, offer_link: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Promo Code
                </label>
                <input
                  type="text"
                  value={formData.promo_code || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, promo_code: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 font-mono"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Logo URL
                </label>
                <input
                  type="url"
                  value={formData.company_logo_url || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      company_logo_url: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Offer Image URL
                </label>
                <input
                  type="url"
                  value={formData.image_url || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, image_url: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Redemption Instructions
              </label>
              <textarea
                value={formData.redemption_instructions || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    redemption_instructions: e.target.value,
                  })
                }
                rows={3}
                placeholder="Step-by-step instructions for claiming this offer..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Legal Disclaimer
              </label>
              <textarea
                value={formData.legal_disclaimer || ""}
                onChange={(e) =>
                  setFormData({ ...formData, legal_disclaimer: e.target.value })
                }
                rows={2}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Terms & Conditions
              </label>
              <textarea
                value={formData.terms_and_conditions || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    terms_and_conditions: e.target.value,
                  })
                }
                rows={4}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}

        {activeTab === "targeting" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand Target
                </label>
                <select
                  value={formData.brand_target}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      brand_target: e.target.value as PartnerOfferBrand,
                    })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {BRAND_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State Type
                </label>
                <select
                  value={formData.offer_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      offer_type: e.target.value as PartnerOfferType,
                    })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.target_all_states}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      target_all_states: e.target.checked,
                    })
                  }
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Available in all states
                </span>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Total Redemptions
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.max_redemptions || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_redemptions: e.target.value
                        ? parseInt(e.target.value)
                        : undefined,
                    })
                  }
                  placeholder="Unlimited"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Redemptions per Member
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.redemptions_per_member || 1}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      redemptions_per_member: parseInt(e.target.value) || 1,
                    })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">
                Offer Visibility Summary
              </h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>
                  Brand:{" "}
                  {
                    BRAND_OPTIONS.find((b) => b.value === formData.brand_target)
                      ?.label
                  }
                </li>
                <li>
                  State Type:{" "}
                  {
                    TYPE_OPTIONS.find((t) => t.value === formData.offer_type)
                      ?.label
                  }
                </li>
                <li>
                  Geographic:{" "}
                  {formData.target_all_states
                    ? "All states"
                    : "Specific states"}
                </li>
                <li>
                  Date Range:{" "}
                  {formData.offer_kind === PartnerOfferKind.LIGHTNING &&
                  formData.start_date &&
                  formData.end_date
                    ? `${formatDate(formData.start_date)} - ${formatDate(formData.end_date)}`
                    : "Always active (Standing)"}
                </li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === "analytics" && (
          <div className="space-y-6">
            {/* Scoring Section */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="font-medium text-gray-900 mb-4">
                Recommendation Scoring
              </h3>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-gray-500">Priority Score</div>
                  <div className="text-xl font-bold">
                    {offer.priority_score}
                  </div>
                  <div className="text-xs text-gray-400">
                    Set by marketing (30%)
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">External Score</div>
                  <div className="text-xl font-bold">
                    {offer.external_score?.toFixed(1) ?? "50.0"}
                  </div>
                  <div className="text-xs text-gray-400">
                    From analytics (70%)
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Effective Score</div>
                  <div className="text-xl font-bold text-primary-600">
                    {offer.effective_score?.toFixed(1) ?? "-"}
                  </div>
                  <div className="text-xs text-gray-400">Used for ranking</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Score Source</div>
                  <div className="text-sm font-medium">
                    {offer.score_source || "default"}
                  </div>
                  {offer.score_updated_at && (
                    <div className="text-xs text-gray-400">
                      Updated {formatDate(offer.score_updated_at)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-medium text-gray-900 mb-4">Performance</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Impressions</span>
                    <span className="font-medium">
                      {offer.total_impressions}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Clicks</span>
                    <span className="font-medium">{stats.clicks}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Claims</span>
                    <span className="font-medium">{offer.total_claims}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Click Rate</span>
                    <span className="font-medium">
                      {offer.total_impressions > 0
                        ? (
                            (stats.clicks / offer.total_impressions) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Conversion Rate</span>
                    <span className="font-medium">
                      {stats.clicks > 0
                        ? ((offer.total_claims / stats.clicks) * 100).toFixed(1)
                        : 0}
                      %
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-medium text-gray-900 mb-4">
                  Offer Details
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created</span>
                    <span>{formatDate(offer.created_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Updated</span>
                    <span>{formatDate(offer.updated_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">External ID</span>
                    <span className="font-mono">
                      {offer.external_id || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Internal ID</span>
                    <span className="font-mono text-xs">{offer.id}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerOfferDetail;
